import React, { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, resolveImage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Package, Users, Tag, ShoppingCart,
  BookOpen, Wallet, FileBarChart, ScrollText, LogOut, ChevronDown, Menu, X,
  Boxes, Truck, Route as RouteIcon, ClipboardList, Settings as SettingsIcon,
  TrendingUp, Download, Megaphone,
} from "lucide-react";
import OmkarLogo from "@/components/OmkarLogo";
import PWAInstallBanner from "@/components/PWAInstallBanner";

const ADMIN_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", permKey: null },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products", permKey: "products" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers", permKey: "customers" },
  { to: "/pricing", label: "Pricing Matrix", icon: Tag, testid: "nav-pricing", permKey: "pricing" },
  { to: "/orders", label: "Orders", icon: ShoppingCart, testid: "nav-orders", permKey: "orders" },
  { to: "/inventory", label: "Inventory", icon: Boxes, testid: "nav-inventory", permKey: "inventory" },
  { to: "/deliveries", label: "Deliveries", icon: ClipboardList, testid: "nav-deliveries", permKey: "deliveries" },
  { to: "/routes", label: "Routes", icon: RouteIcon, testid: "nav-routes", permKey: "routes" },
  { to: "/drivers", label: "Drivers", icon: Truck, testid: "nav-drivers", permKey: "drivers" },
  { to: "/payments", label: "Payments", icon: Wallet, testid: "nav-payments", permKey: "payments" },
  { to: "/ledger", label: "Ledger", icon: BookOpen, testid: "nav-ledger", permKey: "ledger" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testid: "nav-reports", permKey: "reports" },
  { to: "/marketing", label: "Marketing", icon: Megaphone, testid: "nav-marketing", permKey: "marketing" },
  { to: "/audit", label: "Audit Logs", icon: ScrollText, testid: "nav-audit", permKey: "audit" },
  { to: "/analytics", label: "Analytics", icon: TrendingUp, testid: "nav-analytics", permKey: "analytics" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", permKey: "settings" },
];

const CUSTOMER_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/orders", label: "My Orders", icon: ShoppingCart, testid: "nav-orders" },
  { to: "/ledger", label: "My Ledger", icon: BookOpen, testid: "nav-ledger" },
  { to: "/payments", label: "Payments", icon: Wallet, testid: "nav-payments" },
];

const DRIVER_NAV = [
  { to: "/driver", label: "Dashboard", icon: LayoutDashboard, testid: "nav-driver-dashboard" },
  { to: "/driver/deliveries", label: "My Deliveries", icon: Truck, testid: "nav-driver-deliveries" },
];

const ROLE_LABEL = { admin: "Admin Console", customer: "Customer Portal", driver: "Driver App" };

export default function AppShell() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [branding, setBranding] = useState(null);

  const nav = user?.role === "admin"
    ? ADMIN_NAV.filter(n => {
        if (!n.permKey || user?.is_super_admin) return true;
        if (!user?.permissions) return true;
        return user.permissions[n.permKey] !== false;
      })
    : user?.role === "driver" ? DRIVER_NAV : CUSTOMER_NAV;

  useEffect(() => {
    api.get("/whitelabel")
      .then(res => setBranding(res.data))
      .catch(() => {});
  }, []);

  const companyName = branding?.company_name || "Omkar Milk Distributor";
  const rawTagline = branding?.tagline || "";
  const showTagline = rawTagline && !companyName.toLowerCase().includes(rawTagline.toLowerCase());
  const logoUrl = branding?.logo_url ? resolveImage(branding.logo_url) : null;

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      <aside
        className={`fixed md:static z-40 top-0 left-0 h-full w-64 bg-white border-r border-slate-200 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center gap-3 px-5 border-b border-slate-200">
          {logoUrl ? (
            <img src={logoUrl} alt={companyName} className="h-8 max-w-[40px] object-contain shrink-0" />
          ) : (
            <OmkarLogo size={36} />
          )}
          <div className="min-w-0 flex-1">
            <div className="font-heading font-bold text-sm leading-tight tracking-tight text-slate-900 truncate">{companyName}</div>
            {showTagline && (
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5 truncate">{rawTagline}</div>
            )}
          </div>
          <button className="ml-auto md:hidden text-slate-500" onClick={()=>setOpen(false)} aria-label="Close sidebar">
            <X size={20}/>
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({isActive}) => `nav-link ${isActive ? "active" : ""}`}
              onClick={() => setOpen(false)}
              end={n.to === "/dashboard" || n.to === "/driver"}
            >
              <n.icon size={16} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-4 py-3 border-t border-slate-200 text-[11px] text-slate-500">
          Omkar Milk Distributor • v1.1
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <PWAInstallBanner />
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 md:px-6 sticky top-0 z-30">
          <button className="md:hidden mr-3 text-slate-600" onClick={()=>setOpen(true)} aria-label="Open sidebar">
            <Menu size={22}/>
          </button>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500">
            {ROLE_LABEL[user?.role] || "Portal"}
          </div>
          <div className="ml-auto flex items-center gap-3">
            <Button
              onClick={() => {
                const installBtn = document.querySelector('[data-testid="install-app-banner-btn"]');
                if (installBtn) installBtn.click();
                else window.dispatchEvent(new CustomEvent('open-pwa-guide'));
              }}
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs px-3 py-1.5 h-8 gap-1.5 shadow-sm rounded-lg"
              data-testid="header-install-pwa-btn"
            >
              <Download size={14} />
              <span className="hidden sm:inline">Install App</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-slate-200" data-testid="user-menu-trigger">
                  <div className="w-6 h-6 bg-cobalt-500 text-white rounded-full flex items-center justify-center text-[11px] font-semibold mr-2">
                    {user?.name?.slice(0,1).toUpperCase()}
                  </div>
                  <span className="text-sm font-medium text-slate-700 max-w-[140px] truncate">{user?.name}</span>
                  <ChevronDown size={14} className="ml-2 text-slate-400"/>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-xs text-slate-500">Signed in as</div>
                  <div className="text-sm font-semibold">{user?.username}</div>
                  <div className="text-[11px] uppercase tracking-wider text-cobalt-600 mt-0.5">{user?.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                  <LogOut size={14} className="mr-2"/> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      {open && <div className="fixed inset-0 bg-black/30 z-30 md:hidden" onClick={()=>setOpen(false)} />}
    </div>
  );
}
